/**
 * Renders `question.chart` (bar, line, pie) as inline SVG. No chart library —
 * colors come from the app's existing `--cat-*` category tokens, so charts
 * are theme-aware for free. See `src/utils/chartScale.js` for the pure math.
 */

import { el, svgEl } from '../utils/dom.js';
import { niceScale, scaleTicks, pieSectors, pieSectorPath } from '../utils/chartScale.js';
import { formatNumber } from '../utils/format.js';

const WIDTH = 640;
const HEIGHT = 320;
const MARGIN = { top: 24, right: 16, bottom: 40, left: 56 };
const PLOT_WIDTH = WIDTH - MARGIN.left - MARGIN.right;
const PLOT_HEIGHT = HEIGHT - MARGIN.top - MARGIN.bottom;
const PLOT_BOTTOM = MARGIN.top + PLOT_HEIGHT;

/** Cycled for series/sectors that don't specify their own colorToken. */
const CAT_TOKENS = ['data', 'algebra', 'arithmetic', 'geometry', 'combinatorics', 'advanced'];

function seriesColor(index, colorToken) {
  return `var(--cat-${colorToken || CAT_TOKENS[index % CAT_TOKENS.length]})`;
}

function renderLegend(items) {
  return el(
    'ul.chart-legend',
    {},
    items.map((item) =>
      el('li.chart-legend__item', {}, [
        el('span.chart-legend__swatch', { style: { background: item.color } }),
        el('span', { text: item.label })
      ])
    )
  );
}

function renderAxisChart(chart, kind) {
  const categories = chart.categories || [];
  const series = chart.series || [];
  const dataMax = Math.max(0, ...series.flatMap((s) => s.data));
  const scale = niceScale(dataMax);
  const valueToY = (value) => PLOT_BOTTOM - (value / scale.max) * PLOT_HEIGHT;
  const categoryWidth = PLOT_WIDTH / categories.length;

  const gridlines = scaleTicks(scale).map((tick) => {
    const y = valueToY(tick);
    return svgEl('g', {}, [
      svgEl('line', { x1: MARGIN.left, x2: MARGIN.left + PLOT_WIDTH, y1: y, y2: y, class: 'chart-gridline' }),
      svgEl('text', {
        x: MARGIN.left - 8,
        y,
        class: 'chart-axis-label',
        'text-anchor': 'end',
        'dominant-baseline': 'middle',
        text: formatNumber(tick)
      })
    ]);
  });

  const categoryLabels = categories.map((label, i) =>
    svgEl('text', {
      x: MARGIN.left + categoryWidth * (i + 0.5),
      y: PLOT_BOTTOM + 20,
      class: 'chart-axis-label',
      'text-anchor': 'middle',
      text: String(label)
    })
  );

  let plotContent;
  if (kind === 'bar') {
    const groupPad = categoryWidth * 0.16;
    const barGroupWidth = categoryWidth - groupPad;
    const gap = series.length > 1 ? 4 : 0;
    const barWidth = (barGroupWidth - gap * (series.length - 1)) / series.length;
    plotContent = series.flatMap((s, si) =>
      s.data.map((value, ci) => {
        const x = MARGIN.left + ci * categoryWidth + groupPad / 2 + si * (barWidth + gap);
        const y = valueToY(value);
        return svgEl('g', {}, [
          svgEl('rect', {
            x,
            y,
            width: Math.max(barWidth, 0),
            height: Math.max(PLOT_BOTTOM - y, 0),
            rx: 2,
            class: 'chart-bar',
            style: { fill: seriesColor(si, s.colorToken) }
          }),
          // Exact values: questions ask for arithmetic the gridlines alone can't support.
          svgEl('text', { x: x + barWidth / 2, y: y - 4, class: 'chart-value-label', 'text-anchor': 'middle', text: formatNumber(value) })
        ]);
      })
    );
  } else {
    plotContent = series.map((s, si) => {
      const color = seriesColor(si, s.colorToken);
      const points = s.data.map((value, ci) => ({
        x: MARGIN.left + categoryWidth * (ci + 0.5),
        y: valueToY(value)
      }));
      const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
      // Value labels sit above each point, except the lowest series at that category goes
      // below, so two lines running close together don't print on top of each other.
      const isLowest = (ci) => series.length > 1 && series.every((other, oi) => oi === si || other.data[ci] > s.data[ci]);
      return svgEl('g', {}, [
        svgEl('path', { d, class: 'chart-line', style: { stroke: color } }),
        ...points.map((p) => svgEl('circle', { cx: p.x, cy: p.y, r: 4, class: 'chart-point', style: { fill: color } })),
        ...points.map((p, ci) =>
          svgEl('text', {
            x: p.x,
            y: isLowest(ci) ? p.y + 16 : p.y - 9,
            class: 'chart-value-label',
            'text-anchor': 'middle',
            text: formatNumber(s.data[ci])
          })
        )
      ]);
    });
  }

  const svg = svgEl('svg', { viewBox: `0 0 ${WIDTH} ${HEIGHT}`, class: 'chart-svg', 'aria-hidden': 'true' }, [
    ...gridlines,
    ...categoryLabels,
    ...plotContent
  ]);

  const legend = series.length > 1
    ? renderLegend(series.map((s, i) => ({ label: s.label, color: seriesColor(i, s.colorToken) })))
    : null;

  return el('div.chart-body', {}, [svg, legend]);
}

function renderPie(chart) {
  const categories = chart.categories || [];
  const values = chart.series?.[0]?.data || [];
  const sectors = pieSectors(values);
  const cx = 160;
  const cy = 160;
  const radius = 130;

  const paths = sectors.map((sector, i) =>
    svgEl('path', {
      d: pieSectorPath(cx, cy, radius, sector.startAngle, sector.endAngle),
      class: 'chart-pie-sector',
      style: { fill: `var(--cat-${CAT_TOKENS[i % CAT_TOKENS.length]})` }
    })
  );

  const svg = svgEl('svg', { viewBox: '0 0 320 320', class: 'chart-svg chart-svg--pie', 'aria-hidden': 'true' }, paths);

  const legend = renderLegend(
    categories.map((label, i) => ({
      label: `${label} — ${formatNumber(Math.round(sectors[i]?.percent || 0))}%`,
      color: `var(--cat-${CAT_TOKENS[i % CAT_TOKENS.length]})`
    }))
  );

  return el('div.chart-body.chart-body--pie', {}, [svg, legend]);
}

export function chartWidget(chart) {
  if (!chart) return null;
  const body = chart.type === 'pie' ? renderPie(chart) : renderAxisChart(chart, chart.type === 'line' ? 'line' : 'bar');
  return el('div.chart-wrap', {}, [
    chart.caption ? el('div.chart-caption', { text: chart.caption }) : null,
    body,
    chart.ariaSummary ? el('p.sr-only', { text: chart.ariaSummary }) : null
  ]);
}
